import { useCallback, useEffect, useMemo, useState } from "react";
import "./AdminPostAds.css";
import SponsoredAd from "./SponsoredAd";
import { adminAds } from "../../services/adsApi";

// =====================================================
// ADMIN — POST AN ADVERTISEMENT
// The admin's own Text / Image / Video ads, posted straight to Read Blogs —
// no review, no payment (that flow is the public "Advertise with us" one,
// listed further down the page).
//
//   Text   headline, description (at least one), destination URL, CTA
//   Image  image (required), headline, description, destination URL, CTA
//   Video  video upload or direct video URL (required), headline,
//          description, destination URL, CTA
//   CTA    any text the admin types; Learn More / Visit Now / Watch Now are
//          one-tap suggestions
//   Targeting — City, Language, Category — each optional; every one chosen
//   must match the reader; none chosen = every user. Under each choice: how
//   many registered members it covers, and how many match all of them.
//   The list shows how many members each ad has REACHED (actually saw it).
//
// The server validates everything again (models/AdminAd.js).
// =====================================================

const TYPES = [
  ["text", "📝 Text Ad"],
  ["image", "🖼️ Image Ad"],
  ["video", "🎬 Video Ad"],
];
const TYPE_LABEL = { text: "Text", image: "Image", video: "Video" };
const CTA_SUGGESTIONS = {
  text: ["Learn More", "Visit Now"],
  image: ["Learn More", "Visit Now"],
  video: ["Watch Now", "Visit Now"],
};
const members = (n) => `${Number(n || 0).toLocaleString("en-IN")} member${n === 1 ? "" : "s"}`;
const EMPTY = {
  adType: "text",
  title: "",
  description: "",
  ctaText: "",
  destinationUrl: "",
  videoSource: "upload", // "upload" | "url"
  videoUrl: "",
  targetCity: "",
  targetLanguage: "",
  targetCategory: "",
};

const isHttps = (value) => {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
};

const fmtDate = (d) => (d ? new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

export const audienceOf = (ad) => {
  const parts = [ad.targetCity, ad.targetLanguage, ad.targetCategory].filter(Boolean);
  return parts.length ? parts.join(" · ") : "All users";
};

function AdminPostAds() {
  const [form, setForm] = useState(EMPTY);
  const [imageFile, setImageFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [fileKey, setFileKey] = useState(0); // remounts the file inputs on reset
  const [options, setOptions] = useState({ cities: [], languages: [], categories: [] });
  const [posted, setPosted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [posting, setPosting] = useState(false);
  const [formMsg, setFormMsg] = useState({ text: "", ok: false });
  const [busyId, setBusyId] = useState(null);
  const [audience, setAudience] = useState(null);

  // Registered members behind the chosen targeting; the latest choice wins.
  useEffect(() => {
    let current = true;
    adminAds
      .audience({ city: form.targetCity, language: form.targetLanguage, category: form.targetCategory })
      .then((data) => current && setAudience(data.audience || null))
      .catch(() => current && setAudience(null));
    return () => {
      current = false;
    };
  }, [form.targetCity, form.targetLanguage, form.targetCategory]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const load = useCallback(async () => {
    setListError("");
    try {
      const data = await adminAds.listPosted();
      setPosted(data.ads || []);
      if (data.options) setOptions(data.options);
    } catch (err) {
      setListError(err?.response?.data?.message || "Unable to load your advertisements. Please press Refresh.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Local previews of the chosen files.
  const imagePreview = useMemo(() => (imageFile ? URL.createObjectURL(imageFile) : ""), [imageFile]);
  const videoPreview = useMemo(() => (videoFile ? URL.createObjectURL(videoFile) : ""), [videoFile]);
  useEffect(() => () => imagePreview && URL.revokeObjectURL(imagePreview), [imagePreview]);
  useEffect(() => () => videoPreview && URL.revokeObjectURL(videoPreview), [videoPreview]);

  const pickType = (adType) => {
    setForm((f) => ({ ...f, adType, ctaText: adType === "video" ? CTA_SUGGESTIONS.video[0] : f.adType === "video" ? "" : f.ctaText }));
    setFormMsg({ text: "", ok: false });
  };

  const usesVideoUrl = form.adType === "video" && form.videoSource === "url";
  const previewAd = {
    adType: form.adType,
    businessName: "",
    title: form.title.trim(),
    description: form.description.trim(),
    ctaText: form.ctaText.trim(),
    destinationUrl: isHttps(form.destinationUrl.trim()) ? form.destinationUrl.trim() : "",
    logoUrl: "",
    imageUrl: imagePreview,
    videoUrl: usesVideoUrl ? (isHttps(form.videoUrl.trim()) ? form.videoUrl.trim() : "") : videoPreview,
  };
  const previewReady =
    (form.adType === "text" && (previewAd.title || previewAd.description)) ||
    (form.adType === "image" && imagePreview) ||
    (form.adType === "video" && previewAd.videoUrl);

  // Same rules as the server, so the admin hears about a problem before upload.
  const problem = () => {
    if (form.adType === "text" && !form.title.trim() && !form.description.trim()) {
      return "A Text Ad needs a headline or a description.";
    }
    if (form.adType === "image" && !imageFile) return "Please choose the image for this Image Ad.";
    if (form.adType === "video") {
      if (usesVideoUrl && !form.videoUrl.trim()) return "Please enter the video URL.";
      if (usesVideoUrl && !isHttps(form.videoUrl.trim())) return "Video URL must start with https://";
      if (!usesVideoUrl && !videoFile) return "Please choose the video for this Video Ad.";
    }
    if (form.destinationUrl.trim() && !isHttps(form.destinationUrl.trim())) {
      return "Destination URL must start with https://";
    }
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const issue = problem();
    if (issue) {
      setFormMsg({ text: issue, ok: false });
      return;
    }
    const fd = new FormData();
    fd.append("adType", form.adType);
    for (const field of ["title", "description", "ctaText", "destinationUrl", "targetCity", "targetLanguage", "targetCategory"]) {
      const value = form[field].trim();
      if (value) fd.append(field, value);
    }
    if (form.adType === "image") fd.append("image", imageFile);
    if (form.adType === "video") {
      if (usesVideoUrl) fd.append("videoUrl", form.videoUrl.trim());
      else fd.append("video", videoFile);
    }

    setPosting(true);
    setFormMsg({ text: "", ok: false });
    try {
      const data = await adminAds.createPosted(fd);
      setFormMsg({ text: data?.message || "Advertisement posted.", ok: true });
      setForm({ ...EMPTY });
      setImageFile(null);
      setVideoFile(null);
      setFileKey((k) => k + 1);
      await load();
    } catch (err) {
      setFormMsg({ text: err?.response?.data?.message || "The advertisement could not be posted. Please try again.", ok: false });
    } finally {
      setPosting(false);
    }
  };

  const toggle = async (ad) => {
    setBusyId(ad.id);
    try {
      await adminAds.setPostedStatus(ad.id, ad.status === "active" ? "paused" : "active");
      await load();
    } catch (err) {
      setListError(err?.response?.data?.message || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (ad) => {
    if (!window.confirm(`Delete this ${TYPE_LABEL[ad.adType]} Ad? It will no longer be shown in Read Blogs.`)) return;
    setBusyId(ad.id);
    try {
      await adminAds.deletePosted(ad.id);
      await load();
    } catch (err) {
      setListError(err?.response?.data?.message || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      {/* ══ POST AN ADVERTISEMENT ══ */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2 className="admin-section-title">➕ Post an Advertisement</h2>
        </div>
        <p className="apa-intro">
          Your ad goes live in <strong>Read Blogs</strong> as soon as you post it — no review or payment needed.
        </p>

        <form className="apa-form" onSubmit={submit} noValidate>
          <div className="apa-types" role="radiogroup" aria-label="Advertisement type">
            {TYPES.map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={form.adType === value}
                className={`apa-type ${form.adType === value ? "apa-type-on" : ""}`}
                onClick={() => pickType(value)}
              >
                {label}
              </button>
            ))}
          </div>

          {form.adType === "image" && (
            <label className="apa-field">
              <span className="apa-label">Image <em>required</em></span>
              <input
                key={`img-${fileKey}`}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              />
              <span className="apa-hint">JPG, PNG or WEBP, up to 5MB.</span>
            </label>
          )}

          {form.adType === "video" && (
            <div className="apa-field">
              <span className="apa-label">Video <em>required</em></span>
              <div className="apa-inline">
                <label>
                  <input
                    type="radio"
                    name="apa-video-source"
                    checked={form.videoSource === "upload"}
                    onChange={() => setForm((f) => ({ ...f, videoSource: "upload" }))}
                  />{" "}
                  Upload a video
                </label>
                <label>
                  <input
                    type="radio"
                    name="apa-video-source"
                    checked={form.videoSource === "url"}
                    onChange={() => setForm((f) => ({ ...f, videoSource: "url" }))}
                  />{" "}
                  Video URL
                </label>
              </div>
              {usesVideoUrl ? (
                <>
                  <input
                    className="admin-search apa-input"
                    type="url"
                    placeholder="https://… .mp4"
                    aria-label="Video URL"
                    value={form.videoUrl}
                    onChange={set("videoUrl")}
                  />
                  <span className="apa-hint">A direct https:// link to an MP4, WEBM or MOV file.</span>
                </>
              ) : (
                <>
                  <input
                    key={`vid-${fileKey}`}
                    type="file"
                    aria-label="Video file"
                    accept="video/mp4,video/webm,video/quicktime"
                    onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                  />
                  <span className="apa-hint">MP4, WEBM or MOV, up to 50MB.</span>
                </>
              )}
            </div>
          )}

          <label className="apa-field">
            <span className="apa-label">Headline <em>optional</em></span>
            <input className="admin-search apa-input" maxLength={80} value={form.title} onChange={set("title")} />
          </label>

          <label className="apa-field">
            <span className="apa-label">Description <em>optional</em></span>
            <textarea className="admin-search apa-input apa-textarea" rows={3} maxLength={300} value={form.description} onChange={set("description")} />
          </label>

          <label className="apa-field">
            <span className="apa-label">Destination URL <em>optional</em></span>
            <input
              className="admin-search apa-input"
              type="url"
              placeholder="https://example.com"
              value={form.destinationUrl}
              onChange={set("destinationUrl")}
            />
            <span className="apa-hint">Readers who click the ad go straight here. Leave empty for an ad that is not clickable.</span>
          </label>

          <label className="apa-field">
            <span className="apa-label">
              CTA Button Text <em>optional</em>
            </span>
            <input
              className="admin-search apa-input"
              maxLength={25}
              placeholder="Type any text, e.g. Order Now"
              value={form.ctaText}
              onChange={set("ctaText")}
            />
          </label>
          <div className="apa-chips" aria-label="CTA suggestions">
            {CTA_SUGGESTIONS[form.adType].map((c) => (
              <button
                key={c}
                type="button"
                className={`apa-chip ${form.ctaText === c ? "apa-chip-on" : ""}`}
                onClick={() => setForm((f) => ({ ...f, ctaText: c }))}
              >
                {c}
              </button>
            ))}
          </div>
          <span className="apa-hint">Type your own text or tap a suggestion. The button is shown only when a Destination URL is given.</span>

          <fieldset className="apa-targeting">
            <legend className="apa-label">Targeting <em>optional</em></legend>
            <div className="apa-target-grid">
              <label className="apa-field">
                <span className="apa-label">City</span>
                <select className="admin-search apa-input" value={form.targetCity} onChange={set("targetCity")}>
                  <option value="">All Cities</option>
                  {options.cities.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                {form.targetCity && audience?.city != null && (
                  <span className="apa-count">👥 {members(audience.city)} in {form.targetCity}</span>
                )}
              </label>
              <label className="apa-field">
                <span className="apa-label">Language</span>
                <select className="admin-search apa-input" value={form.targetLanguage} onChange={set("targetLanguage")}>
                  <option value="">All Languages</option>
                  {options.languages.map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
                {form.targetLanguage && audience?.language != null && (
                  <span className="apa-count">👥 {members(audience.language)} read {form.targetLanguage}</span>
                )}
              </label>
              <label className="apa-field">
                <span className="apa-label">Category</span>
                <select className="admin-search apa-input" value={form.targetCategory} onChange={set("targetCategory")}>
                  <option value="">All Categories</option>
                  {options.categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                {form.targetCategory && audience?.category != null && (
                  <span className="apa-count">👥 {members(audience.category)} like {form.targetCategory}</span>
                )}
              </label>
            </div>
            <span className="apa-hint">
              Shown to: <strong>{audienceOf(form)}</strong>
              {audience && (
                <>
                  {" "}— <strong className="apa-count-total">{members(audience.matching)}</strong> registered{" "}
                  {audience.matching === audience.total ? "(everyone)" : `of ${members(audience.total)}`}
                </>
              )}
              . Every filter you choose must match the reader; choose none to show the ad to all users. City is the
              city recorded from each member's login.
            </span>
          </fieldset>

          {previewReady && (
            <div className="apa-preview">
              <span className="apa-label">Preview — as readers will see it in Read Blogs</span>
              <SponsoredAd ad={previewAd} />
            </div>
          )}

          {formMsg.text && (
            <p className={`apa-msg ${formMsg.ok ? "apa-msg-ok" : ""}`} role={formMsg.ok ? "status" : "alert"}>
              {formMsg.text}
            </p>
          )}
          <button type="submit" className="admin-btn btn-success apa-submit" disabled={posting}>
            {posting ? "Posting…" : "Post Advertisement"}
          </button>
        </form>
      </div>

      {/* ══ POSTED ADVERTISEMENTS ══ */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2 className="admin-section-title">📋 Your Posted Advertisements</h2>
          <button className="admin-btn" onClick={load}>Refresh</button>
        </div>
        {listError && <p className="apa-msg" role="alert">{listError}</p>}
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Ad</th>
                <th>Shown to</th>
                <th>Reached</th>
                <th>Status</th>
                <th>Posted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="admin-empty">Loading…</td></tr>
              ) : posted.length === 0 ? (
                <tr><td colSpan={7} className="admin-empty">No advertisements posted yet</td></tr>
              ) : (
                posted.map((ad, i) => (
                  <tr key={ad.id}>
                    <td className="admin-td-num">{i + 1}</td>
                    <td>
                      <div>{ad.title || ad.description || <span className="admin-td-muted">(no headline)</span>}</div>
                      <div className="admin-td-muted">
                        {TYPE_LABEL[ad.adType]} Ad
                        {ad.destinationUrl ? (
                          <>
                            {" · "}
                            <a href={ad.destinationUrl} target="_blank" rel="noopener noreferrer nofollow">{ad.destinationUrl}</a>
                          </>
                        ) : (
                          " · no destination (not clickable)"
                        )}
                      </div>
                    </td>
                    <td className="admin-td-muted">{audienceOf(ad)}</td>
                    <td>
                      <strong className="apa-reach">{members(ad.reach)}</strong>
                    </td>
                    <td>
                      <span className={`admin-badge ${ad.status === "active" ? "badge-active" : ""}`}>
                        {ad.status === "active" ? "Live" : "Paused"}
                      </span>
                    </td>
                    <td className="admin-td-muted">{fmtDate(ad.createdAt)}</td>
                    <td>
                      <div className="apa-row-actions">
                        <button
                          className={`admin-btn ${ad.status === "active" ? "btn-warn" : "btn-success"}`}
                          disabled={busyId === ad.id}
                          onClick={() => toggle(ad)}
                        >
                          {ad.status === "active" ? "Pause" : "Resume"}
                        </button>
                        <button className="admin-btn btn-danger" disabled={busyId === ad.id} onClick={() => remove(ad)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default AdminPostAds;
